<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import NextButton from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'chooseTime']);
const { t, locale } = useI18n();
const selection = ref('an_hour_from_now');
const customDate = ref('');
const error = ref(false);
const options = [
  ['an_hour_from_now', 'HOUR'],
  ['until_tomorrow', 'TOMORROW'],
  ['until_next_week', 'WEEK'],
  ['until_next_reply', 'REPLY'],
  ['custom', 'CUSTOM'],
];
const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const minimum = format(new Date(), "yyyy-MM-dd'T'HH:mm");
const selectedDate = computed(() => {
  if (selection.value === 'until_next_reply') return null;
  if (selection.value === 'custom')
    return customDate.value ? new Date(customDate.value) : null;
  return new Date(findSnoozeTime(selection.value) * 1000);
});
const preview = computed(() => {
  if (selection.value === 'until_next_reply')
    return t('CONVERSATION.CUSTOM_SNOOZE.REPLY');
  if (!selectedDate.value || Number.isNaN(selectedDate.value.getTime()))
    return '—';
  return new Intl.DateTimeFormat(
    locale.value.startsWith('en') ? 'en' : 'pt-BR',
    { dateStyle: 'full', timeStyle: 'short' }
  ).format(selectedDate.value);
});
const chooseTime = () => {
  // Recompute quick choices on confirmation, even when the modal stayed open.
  const date =
    selection.value === 'custom'
      ? selectedDate.value
      : new Date(findSnoozeTime(selection.value) * 1000);
  if (selection.value === 'until_next_reply') {
    emit('chooseTime', null);
    return;
  }
  if (!date || Number.isNaN(date.getTime()) || date <= new Date()) {
    error.value = true;
    return;
  }
  emit('chooseTime', date);
};
</script>

<template>
  <form
    class="flex flex-col gap-5 bg-n-solid-2 p-5 sm:p-6"
    @submit.prevent="chooseTime"
  >
    <header class="flex items-start gap-3 pe-6">
      <div
        class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-n-alpha-2 text-n-blue-11"
        aria-hidden="true"
      >
        <span class="i-hugeicons-calendar-03 size-6" />
      </div>
      <div>
        <h2 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ t('CONVERSATION.CUSTOM_SNOOZE.TITLE') }}
        </h2>
        <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-11">
          {{ t('CONVERSATION.CUSTOM_SNOOZE.DESCRIPTION') }}
        </p>
      </div>
    </header>
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <label
        v-for="[value, key] in options"
        :key="value"
        class="flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm text-n-slate-12 focus-within:ring-2 focus-within:ring-n-brand"
        :class="
          selection === value
            ? 'border-n-brand bg-n-alpha-2'
            : 'border-n-weak hover:bg-n-alpha-2'
        "
      >
        <input
          v-model="selection"
          type="radio"
          name="snooze-choice"
          :value="value"
          @change="error = false"
        />
        {{ t(`CONVERSATION.CUSTOM_SNOOZE.${key}`) }}
      </label>
    </div>
    <label
      v-if="selection === 'custom'"
      class="flex flex-col gap-2 text-sm font-medium text-n-slate-12"
    >
      {{ t('CONVERSATION.CUSTOM_SNOOZE.DATE') }}
      <input
        v-model="customDate"
        type="datetime-local"
        :min="minimum"
        required
        :aria-invalid="error"
        class="!mb-0 w-full min-w-0 rounded-lg border border-n-weak bg-n-solid-2 p-3 text-n-slate-12 [color-scheme:light] dark:[color-scheme:dark]"
        @input="error = false"
      />
    </label>
    <div
      class="rounded-xl border border-n-weak bg-n-alpha-2 p-4"
      aria-live="polite"
    >
      <p class="m-0 text-xs font-medium text-n-slate-11">
        {{ t('CONVERSATION.CUSTOM_SNOOZE.PREVIEW') }}
      </p>
      <p class="mb-0 mt-1 text-sm font-semibold text-n-slate-12">
        {{ preview }}
      </p>
      <p class="mb-0 mt-2 text-xs text-n-slate-11">
        {{ t('CONVERSATION.CUSTOM_SNOOZE.TIMEZONE', { zone }) }}
      </p>
    </div>
    <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ t('CONVERSATION.CUSTOM_SNOOZE.INVALID') }}
    </p>
    <footer
      class="flex flex-wrap justify-end gap-2 border-t border-n-weak pt-4"
    >
      <NextButton
        type="button"
        faded
        slate
        :label="t('CONVERSATION.CUSTOM_SNOOZE.CANCEL')"
        @click="emit('close')"
      />
      <NextButton
        type="submit"
        icon="i-hugeicons-calendar-03"
        :label="t('CONVERSATION.CUSTOM_SNOOZE.APPLY')"
      />
    </footer>
  </form>
</template>
